import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, CheckCircle2, Quote, Info } from 'lucide-react';
import { track, EVENTS } from '../lib/analytics';

/**
 * GuideBody — renders a structured editorial article body for sourcing guides.
 * Block schema (see /src/data/guideBodies.js):
 *   { type: 'p' | 'h2' | 'h3', text }
 *   { type: 'ul' | 'ol', items: [string] }
 *   { type: 'pullquote', text, attribution }
 *   { type: 'callout', title, text }
 *   { type: 'table', headers: [string], rows: [[string]] }
 *   { type: 'inlineLink', label, url }
 *   { type: 'keyTakeaways', items: [string] }
 *   { type: 'cta', title, body, primary: {label, url}, secondary?: {label, url} }
 */
export const GuideBody = ({ body, guideSlug }) => {
  if (!body) return null;

  return (
    <article className="guide-prose" data-testid="guide-body">
      {/* Executive Summary */}
      {body.executiveSummary && (
        <div className="mb-12 border-l-2 border-black bg-[#F5F5F0] px-6 py-7 md:px-8 md:py-8" data-testid="guide-executive-summary">
          <div className="label-overline text-neutral-600">Executive Summary</div>
          <p className="mt-3 font-serif text-[1.05rem] leading-relaxed text-neutral-800 md:text-[1.125rem]" style={{ fontFamily: '"Bricolage Grotesque", Georgia, serif', fontWeight: 400 }}>
            {body.executiveSummary}
          </p>
        </div>
      )}

      {/* Sections */}
      {body.sections.map((block, idx) => <Block key={idx} block={block} guideSlug={guideSlug} />)}
    </article>
  );
};

const Block = ({ block, guideSlug }) => {
  switch (block.type) {
    case 'h2':
      return (
        <h2 className="mt-14 mb-5 font-display tracking-tighter text-[1.7rem] leading-[1.05] md:text-[2.1rem]" style={{ fontWeight: 700 }}>
          {block.text}
        </h2>
      );

    case 'h3':
      return (
        <h3 className="mt-9 mb-3 font-display tracking-tight text-[1.2rem] md:text-[1.35rem]" style={{ fontWeight: 600 }}>
          {block.text}
        </h3>
      );

    case 'p':
      return (
        <p className="mb-5 text-[1rem] leading-[1.7] text-neutral-800 md:text-[1.0625rem]">
          {block.text}
        </p>
      );

    case 'ul':
      return (
        <ul className="mb-6 space-y-2.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-[1rem] leading-[1.65] text-neutral-800 md:text-[1.0625rem]">
              <span className="mt-[0.6rem] block h-[3px] w-[14px] shrink-0 bg-black" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'ol':
      return (
        <ol className="mb-6 space-y-2.5 counter-reset-list">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-4 text-[1rem] leading-[1.65] text-neutral-800 md:text-[1.0625rem]">
              <span className="mt-[2px] inline-flex h-6 w-6 shrink-0 items-center justify-center border border-black font-mono-data text-[0.7rem]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );

    case 'pullquote':
      return (
        <blockquote className="my-10 border-y-2 border-black py-8 px-1">
          <Quote size={20} strokeWidth={1.5} className="mb-3 text-neutral-400" />
          <p className="font-display tracking-tight text-[1.4rem] leading-[1.25] md:text-[1.7rem]" style={{ fontWeight: 500 }}>
            "{block.text}"
          </p>
          {block.attribution && (
            <footer className="mt-4 label-overline text-neutral-600">— {block.attribution}</footer>
          )}
        </blockquote>
      );

    case 'callout':
      return (
        <aside className="my-8 border border-black bg-[#0A0A0A] p-6 text-white md:p-8">
          <div className="flex items-start gap-3">
            <Info size={18} strokeWidth={1.5} className="mt-1 shrink-0" />
            <div>
              <div className="label-overline text-neutral-400">{block.title}</div>
              <p className="mt-3 text-[0.95rem] leading-[1.65] text-neutral-200 md:text-[1rem]">{block.text}</p>
            </div>
          </div>
        </aside>
      );

    case 'table':
      return (
        <div className="my-8 overflow-x-auto border border-black">
          <table className="w-full min-w-[600px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-black bg-[#F5F5F0]">
                {block.headers.map((h, i) => (
                  <th key={i} className="border-r border-black px-4 py-3 text-left label-overline last:border-r-0">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-neutral-300 last:border-b-0">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`border-r border-neutral-300 px-4 py-3 text-[0.9rem] text-neutral-800 last:border-r-0 ${ci === 0 ? 'font-medium text-black' : ''}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'inlineLink':
      return (
        <p className="my-6">
          <Link to={block.url} className="inline-flex items-center gap-1.5 border-b border-black pb-0.5 font-medium text-black hover:bg-black hover:text-white">
            {block.label} <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </p>
      );

    case 'keyTakeaways':
      return (
        <div className="my-12 border-2 border-black p-6 md:p-8" data-testid="guide-key-takeaways">
          <div className="label-overline">Key Takeaways</div>
          <ul className="mt-5 space-y-3">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[1rem] leading-[1.55] text-neutral-900">
                <CheckCircle2 size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'cta':
      return (
        <div className="my-12 border border-black bg-[#0A0A0A] p-7 text-white md:p-10" data-testid="guide-cta">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-[36rem]">
              <div className="label-overline text-neutral-400">Take action</div>
              <h3 className="mt-3 font-display tracking-tighter text-[1.5rem] leading-[1.1] md:text-[2rem]" style={{ fontWeight: 600 }}>
                {block.title}
              </h3>
              <p className="mt-3 text-[0.95rem] leading-relaxed text-neutral-300 md:text-[1rem]">{block.body}</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row md:flex-col md:items-end md:gap-2">
              {block.primary && (
                <Link
                  to={block.primary.url}
                  onClick={() => track(EVENTS.GUIDE_CTA_CLICKED, { guide_slug: guideSlug, destination: block.primary.url, action: 'primary' })}
                  className="inline-flex items-center justify-center gap-2 border border-white bg-white px-5 py-3 label-overline text-black hover:bg-transparent hover:text-white"
                  data-testid="guide-cta-primary"
                >
                  {block.primary.label} <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              )}
              {block.secondary && (
                <Link
                  to={block.secondary.url}
                  onClick={() => track(EVENTS.GUIDE_CTA_CLICKED, { guide_slug: guideSlug, destination: block.secondary.url, action: 'secondary' })}
                  className="inline-flex items-center justify-center gap-2 border border-white/40 px-5 py-3 label-overline text-white hover:bg-white hover:text-black"
                  data-testid="guide-cta-secondary"
                >
                  {block.secondary.label} <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              )}
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

/**
 * Extracts FAQ pairs from a body for FAQPage JSON-LD.
 * Walks the sections array and pairs each h3 inside the "FAQs" h2 section
 * with the subsequent p block.
 */
export const extractFaqs = (body) => {
  if (!body || !body.sections) return [];
  const faqs = [];
  let inFaqSection = false;
  let currentQuestion = null;

  for (const block of body.sections) {
    if (block.type === 'h2') {
      inFaqSection = /^FAQs?$/i.test(block.text);
      currentQuestion = null;
      continue;
    }
    if (!inFaqSection) continue;
    if (block.type === 'h3') {
      currentQuestion = block.text;
    } else if (block.type === 'p' && currentQuestion) {
      faqs.push({ question: currentQuestion, answer: block.text });
      currentQuestion = null;
    }
  }
  return faqs;
};

export default GuideBody;
